import React from 'react';
import { MapPin, Phone, Mail, Facebook, MessageSquare } from 'lucide-react';

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const quickLinks = [
    { label: 'Home', id: 'hero' },
    { label: 'About Us', id: 'about' },
    { label: 'Storage Units', id: 'storage' },
    { label: 'Features', id: 'features' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_red-sails-storage/artifacts/p7p4c6x9_RSSS%20Logo%202.jpg"
                alt="Red Sail Logo"
                className="h-12 w-12 object-contain bg-white rounded-full p-1"
                loading="lazy"
                decoding="async"
                style={{ width: '48px', height: '48px', display: 'block' }}
              />
              <div>
                <h3 className="text-white font-bold text-lg">Red Sail Self Storage</h3>
                <p className="text-sm text-gray-400">Secure Container Storage</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed">
              Premium 20ft shipping container storage in St. Osyth, Essex. 
              Secure, weatherproof, and affordable storage solutions for all your needs.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-sm hover:text-red-400 transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Info</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  Blackwater Farm, Lee Wick Lane<br />
                  St. Osyth, Clacton-on-Sea<br />
                  Essex, CO16 8ES
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-red-400 flex-shrink-0" />
                <a href="tel:01255823031" className="text-sm hover:text-red-400 transition-colors">
                  01255 823031
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-red-400 flex-shrink-0" />
                <a
                  href="mailto:redsailselfstorage@gmail.com"
                  className="text-sm hover:text-red-400 transition-colors break-all"
                >
                  redsailselfstorage@gmail.com
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <MessageSquare className="h-5 w-5 text-red-400 flex-shrink-0" />
                <a
                  href="https://wa.me/447811956789"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-red-400 transition-colors"
                >
                  +44 7811 956789
                </a>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h4 className="text-white font-semibold mb-4">Business Hours</h4>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-white font-medium">Office Hours</p>
                <p>Daily: 9:00 AM - 5:00 PM</p>
                <p className="text-gray-400 text-xs mt-1">Other times available on request</p>
              </div>
              <div className="pt-2 border-t border-gray-700">
                <p className="text-white font-medium">Container Access</p>
                <p>Daily: 7:00 AM - 7:00 PM</p>
                <p className="text-gray-400 text-xs mt-1">Extended hours by arrangement</p>
              </div>
            </div>
          </div>
        </div>

        {/* Social & Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-center md:text-left">
              &copy; {new Date().getFullYear()} Red Sail Self Storage Ltd. All rights reserved.
            </p>
            <div className="flex items-center space-x-4">
              <a
                href="https://www.facebook.com/redsailselfstorage/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-red-600 flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://wa.me/447811956789"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-green-600 flex items-center justify-center transition-colors"
                aria-label="WhatsApp"
              >
                <MessageSquare className="h-5 w-5" />
              </a>
            </div>
          </div>
          <div className="text-center mt-4">
            <p className="text-xs text-gray-500">
              All bookings subject to Terms and Conditions. Insurance not provided - customers must arrange their own coverage.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
